export interface BusinessModuleChild {
  key: string;
  title: string;
  path: string;
}

export interface BusinessModuleGroup {
  key: string;
  title: string;
  path?: string;
  children?: BusinessModuleChild[];
}

export const BUSINESS_MODULE_CATALOG: BusinessModuleGroup[] = [
  { key: 'dashboard', title: 'Inicio', path: '/inicio' },
  {
    key: 'content',
    title: 'Contenido',
    children: [
      { key: 'content.courses', title: 'Mis cursos', path: '/products/courses' },
      { key: 'content.ebooks', title: 'Ebooks', path: '/products/ebooks' },
    ],
  },
  {
    key: 'videoteca',
    title: 'Videoteca',
    children: [
      { key: 'videoteca.library', title: 'Mi Videoteca', path: '/Videoteca' },
      { key: 'videoteca.collaborators', title: 'Colaboradores', path: '/Colaboradores' },
    ],
  },
  {
    key: 'crm',
    title: 'CRM',
    children: [
      { key: 'crm.leads', title: 'Contactos', path: '/contactos' },
      { key: 'crm.funnels', title: 'Embudos', path: '/lead-funnels' },
      { key: 'crm.status', title: 'Estados', path: '/lead-status' },
    ],
  },
  { key: 'email_marketing', title: 'Email Marketing', path: '/Email-Marketing' },
  { key: 'landing_pages', title: 'Landing pages', path: '/landing-pages' },
  {
    key: 'appointments',
    title: 'Calendario',
    path: '/calendario',
    children: [
      { key: 'appointments.calendar',      title: 'Calendario',       path: '/calendario' },
      { key: 'appointments.list',          title: 'Todas las citas',  path: '/calendario' },
      { key: 'appointments.types',         title: 'Tipos de cita',    path: '/calendario/configuracion' },
      { key: 'appointments.availability',  title: 'Mi disponibilidad', path: '/calendario/configuracion' },
      { key: 'appointments.integrations',  title: 'Integraciones',    path: '/calendario/configuracion' },
    ],
  },
  {
    key: 'social_media',
    title: 'Redes sociales',
    children: [
      { key: 'social_media.planner', title: 'Planner', path: '/social-media' },
      { key: 'social_media.new', title: 'Nueva publicación', path: '/social-media/nuevo' },
      { key: 'social_media.analytics', title: 'Analytics', path: '/social-media/analytics' },
      { key: 'social_media.connect', title: 'Configuración', path: '/social-media/conectar' },
    ],
  },
  {
    key: 'settings',
    title: 'Configuración',
    children: [
      { key: 'settings.status', title: 'Estados de seguimiento', path: '/lead-status' },
      { key: 'settings.funnels', title: 'Embudos', path: '/lead-funnels' },
    ],
  },
  {
    key: 'administration',
    title: 'Administración',
    children: [
      { key: 'administration.sales', title: 'Ventas', path: '/negocio/ventas' },
      { key: 'administration.payments', title: 'Cobranza', path: '/negocio/pagos' },
    ],
  },
];

export const ALL_BUSINESS_MODULE_KEYS: string[] = BUSINESS_MODULE_CATALOG.flatMap((g) =>
  g.children ? g.children.map((c) => c.key) : [g.key],
);

export const ALWAYS_INCLUDED_PARENT_KEYS: string[] = ['dashboard'];

export const SELECTABLE_PARENT_MODULE_KEYS: string[] = BUSINESS_MODULE_CATALOG
  .map((g) => g.key)
  .filter((k) => !ALWAYS_INCLUDED_PARENT_KEYS.includes(k) && k !== 'settings');

export function resolveParentModuleKey(moduleKey: string): string {
  if (!moduleKey.includes('.')) return moduleKey;
  return moduleKey.split('.')[0];
}

export function isSelectableParentKey(key: string): boolean {
  return SELECTABLE_PARENT_MODULE_KEYS.includes(key);
}

export function flatKeysToParentKeys(flatKeys: string[]): string[] {
  const parents = new Set<string>();
  flatKeys.forEach((key) => {
    const parent = resolveParentModuleKey(key);
    if (isSelectableParentKey(parent)) parents.add(parent);
  });
  return SELECTABLE_PARENT_MODULE_KEYS.filter((k) => parents.has(k));
}

export function expandParentKeysToFlatKeys(parentKeys: string[]): string[] {
  const flat = new Set<string>(ALWAYS_INCLUDED_PARENT_KEYS);
  parentKeys.forEach((parentKey) => {
    const group = BUSINESS_MODULE_CATALOG.find((g) => g.key === parentKey);
    if (!group) return;
    if (group.children?.length) {
      group.children.forEach((c) => flat.add(c.key));
    } else {
      flat.add(group.key);
    }
  });
  return Array.from(flat);
}

export function getModuleGroupTitle(key: string): string {
  for (const group of BUSINESS_MODULE_CATALOG) {
    if (group.key === key) return group.title;
    if (group.children) {
      const child = group.children.find((c) => c.key === key);
      if (child) return child.title;
    }
  }
  return key;
}

/** Módulos incluidos por defecto en plan Gratuita (business) */
export const DEFAULT_FREE_BUSINESS_MODULES: string[] = [
  'dashboard',
  'content.courses',
  'content.ebooks',
  'crm.leads',
  'appointments',
  'videoteca',
  'email_marketing',
  'landing_pages',
  'social_media',
  'administration.sales',
  'administration.payments',
];

/** Asegura módulos nuevos del plan gratuito en sesiones/planes cacheados */
export function mergeLegacyFreeModules(modules: string[]): string[] {
  const hasFreeMarker = DEFAULT_FREE_BUSINESS_MODULES.some((k) => modules.includes(k));
  if (!hasFreeMarker) return modules;
  return Array.from(new Set(modules.concat(DEFAULT_FREE_BUSINESS_MODULES)));
}

export function resolveModuleKeyFromPath(url: string): string | null {
  const path = url.split('?')[0].replace(/\/+$/, '') || '/';
  for (const group of BUSINESS_MODULE_CATALOG) {
    if (group.path && (path === group.path || path.startsWith(group.path + '/'))) {
      return group.key;
    }
    if (group.children) {
      for (const child of group.children) {
        if (path === child.path || path.startsWith(child.path + '/')) {
          return child.key;
        }
      }
    }
  }
  return null;
}

export function hasModuleAccess(enabledModules: string[] | null, moduleKey: string | undefined): boolean {
  if (!moduleKey) return true;
  if (!enabledModules || enabledModules.length === 0) return true;
  if (enabledModules.includes(moduleKey)) return true;
  const parent = moduleKey.split('.')[0];
  if (parent !== moduleKey && enabledModules.includes(parent)) return true;
  return false;
}
